import React, { useEffect, useMemo, useState } from 'react';

import { faBars, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import _find from 'lodash/find';
import _map from 'lodash/map';
import styled from 'styled-components';

import { FileModel } from 'models';

import { useEvent } from 'core/hooks';
import { Button, ButtonVariants, ComponentSizes, DropdownWithValuesField, Icon, Input, Tooltip } from 'core/ui';

import { useMutationFiles } from 'features/api/hooks';
import { SettingsHelpers, useUserSettings } from 'features/settings';
import { WellKnownSettingName } from 'features/settings/types/WellKnownSettingName';

import RotateLeftIcon from '../assets/rotate-left.svg?react';
import RotateRightIcon from '../assets/rotate-right.svg?react';
import { DocumentCategories, SendDocumentMethods, SendDocumentMethodsDicom, SendDocumentMethodsWithEHR } from '../constants';
import { ActiveTab } from './ExamProcessing';

// --- Styled Components --- Moved to top

const BREAKPOINT_MOBILE = '900px';

const StyledDivHeaderWrapper = styled.div<{ $sidebarExpanded: boolean }>`
  display: flex;
  flex-wrap: wrap;
  //justify-content: space-between;
  row-gap: 5px;
  padding: 8px 15px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[300]};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[100]};
  z-index: 1; /* Ensure header stays above content */

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding: 1px;
    height: 25px;
  }

  // Add a spacer to give room for the "expand" button that floats over this header.
  ${({ $sidebarExpanded }) =>
    !$sidebarExpanded &&
    `
  &::before {
    flex: 0 0 auto;
    display: block;
    content: '';
    width: calc(30px); // 30px is the width of the expand button, 15px is the padding.
    height: calc(41px - 8px);
  }
  `}
`;

// hamburger toggle button
const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// wrapper around left-tabs + send-controls
const StyledControlsWrapper = styled.div<{ $open: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    flex-direction: column;
    gap: 8px;
    background: ${({ theme }) => theme.colors.palette.grayscale[2]};
    padding: 8px 0;
  }
`;

const StyledHorizontalSpacer = styled.div`
  flex: 1 1 0;
`;

const StyledDivLeftControls = styled.div`
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
`;

const StyledTabButton = styled(Button).attrs({ variant: ButtonVariants.SECONDARY, size: ComponentSizes.SMALL })<{ $active?: boolean }>``;

const StyledDivSendContainer = styled.div<{ $show: boolean }>`
  flex: 0 1 auto;
  display: ${({ $show }) => ($show ? 'flex' : 'none')}; /* Hide controls when not on files tab */
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

const StyledRotateWrapperDiv = styled.div`
  flex: 0 0 auto;
  display: flex;
`;

const StyledDropdown = styled(DropdownWithValuesField)`
  width: 180px;
  .k-input {
    height: 30px;
    font-size: 12px;
  }
`;

const StyledRotateButtonDiv = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  padding: 4px;
  border-radius: 4px;
  background-color: transparent;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ $disabled, theme }) => ($disabled ? 'transparent' : theme.colors.palette.grayscale[100])};
  }

  svg {
    fill: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const StyledInput = styled(Input)`
  width: 200px;
  height: 30px;
  font-size: 12px;
`;

const StyledSendButton = styled(Button)`
  height: 30px;
  font-size: 12px;
  padding: 0 15px;
`;

// --- End Styled Components ---

// Define placeholder types locally
type Integration = Record<string, unknown>;
type DropdownItem<T> = { name: string; value: T; [key: string]: unknown };

const ROTATE_LEFT_DEGREES = -90;
const ROTATE_RIGHT_DEGREES = 90;

interface ExamProcessingHeaderProps {
  email?: string;
  examId: number;
  fax?: string;
  file: FileModel | null;
  hasReadHistory?: boolean;
  locationHasIntegrations?: boolean;
  locationHasDicomIntegration?: boolean;
  physicianEmail?: string;
  sidebarExpanded?: boolean;
  hasSuid?: boolean;
  integrations?: Integration[];
  isInternal?: boolean;
  setCurrentFile: (file: FileModel | null) => void;
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
}

type ContactType = DropdownItem<string | number>;

const ExamProcessingHeader: React.FC<ExamProcessingHeaderProps> = ({
  email = '',
  examId,
  fax = '',
  file,
  hasReadHistory = false,
  locationHasIntegrations = false,
  locationHasDicomIntegration = false,
  physicianEmail = '',
  sidebarExpanded = true,
  hasSuid = false,
  integrations = [],
  isInternal = false,
  setCurrentFile,
  activeTab,
  onTabChange,
}) => {
  const { userSettings } = useUserSettings(true);
  const ShowGenerateReport = SettingsHelpers.findSettingByName(userSettings, WellKnownSettingName.ShowGenerateReport, false)?.value === 'true';

  const [contact, setContact] = useState<string>('');
  const [contactType, setContactType] = useState<ContactType | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { deleteFileMutation, changeCategoryMutation, generateReportMutation, rotateFileMutation, sendDocumentsMutation } = useMutationFiles(
    examId,
    setCurrentFile,
  );

  const sendButtonDisabled =
    (contactType?.value === 'EHR Integration' || typeof contactType?.value === 'number' ? false : !contact || !contactType) || sendDocumentsMutation.isPending;

  const defaultContactDropdownValue = useMemo(() => {
    if (file?.categoryId === DocumentCategories.EXAM.value) {
      setContact(physicianEmail ?? '');
      return SendDocumentMethods.PHYSICIAN_EMAIL as ContactType;
    }
    if (file?.categoryId === DocumentCategories.FINAL_REPORT.value) {
      if (email) {
        setContact(email);
        return SendDocumentMethods.EMAIL as ContactType;
      }
      setContact(fax ?? '');
      return SendDocumentMethods.FAX as ContactType;
    }
    return { value: null, name: 'Send Document Via' };
  }, [email, fax, file, physicianEmail]);

  const handleContactDropdownChange = useEvent((event: DropDownListChangeEvent) => {
    const primitiveValue = event.value as string | number | null;

    // Find the full object corresponding to the primitive value
    const sendMethods = getSendMethods();
    const selectedItem = sendMethods.find((item) => item.value === primitiveValue) ?? null;

    setContactType(selectedItem); // Store the full object (or null)

    // Update contact input based on primitive value
    if (primitiveValue === 'Email') setContact(email ?? '');
    else if (primitiveValue === 'Physician Email') setContact(physicianEmail ?? '');
    else if (primitiveValue === 'Fax') setContact(fax ?? '');
    else if (primitiveValue === 'EHR Integration' || primitiveValue === 'Dicom' || typeof primitiveValue === 'number' || primitiveValue === null) {
      setContact(''); // Clear for integrations, destinations, or null selection
    }
  });

  const handleContactInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContact(event.target?.value);
  };

  const handleDeleteFile = useEvent(() => {
    if (!file?.id || deleteFileMutation.isPending) return;
    deleteFileMutation.mutate(file.id);
  });

  const handleCategoryChange = useEvent((event: DropDownListChangeEvent) => {
    const newCategoryId = event.value as number | null;
    if (file?.id && newCategoryId !== null && newCategoryId !== file?.categoryId && !changeCategoryMutation.isPending) {
      changeCategoryMutation.mutate({ fileId: file.id, categoryId: newCategoryId });
    }
  });

  const handleSendClick = useEvent(() => {
    if (!contactType || !file?.id || sendDocumentsMutation.isPending) return;

    const sendType = typeof contactType.value === 'string' ? contactType.value : 'Destination';
    const destinationId = typeof contactType?.value === 'number' ? contactType.value : undefined;

    sendDocumentsMutation.mutate({
      fileId: file.id,
      sendType,
      contact,
      destinationId,
    });
  });

  const handleGenerateReport = useEvent(() => {
    if (!file?.id || generateReportMutation.isPending) return;
    generateReportMutation.mutate({ fileId: file.id, examId });
  });

  const handleRotate = useEvent((degrees: number) => {
    if (!file || rotateFileMutation.isPending) return;
    rotateFileMutation.mutate({ file, degrees });
  });

  useEffect(() => {
    const defaultVal = defaultContactDropdownValue;
    if (defaultVal && typeof defaultVal === 'object' && 'value' in defaultVal) {
      if (defaultVal.value === null) {
        setContactType(null);
      } else {
        setContactType(defaultVal);
      }
    } else {
      setContactType(null);
    }
  }, [defaultContactDropdownValue]);

  const getSendMethods = (): DropdownItem<string | number>[] => {
    let dropdownValues: Record<string, DropdownItem<string | number>> = SendDocumentMethods;
    if (locationHasIntegrations) dropdownValues = { ...SendDocumentMethodsWithEHR, ...dropdownValues };
    if (locationHasDicomIntegration) dropdownValues = { ...SendDocumentMethodsDicom, ...dropdownValues };
    const newVals = _map(dropdownValues);
    const integrationOptions = integrations?.map((i) => ({ name: i.name, value: i.destinationId }) as DropdownItem<number>) ?? [];
    return [...newVals, ...integrationOptions];
  };

  return (
    <StyledDivHeaderWrapper $sidebarExpanded={sidebarExpanded}>
      <MobileMenuButton onClick={() => setMobileOpen((o) => !o)}>
        <Icon icon={faBars} />
      </MobileMenuButton>
      <StyledControlsWrapper $open={mobileOpen}>
        <StyledDivLeftControls>
          {isInternal && (
            <>
              <StyledTabButton $active={activeTab === 'files'} onClick={() => onTabChange('files')}>
                Files
              </StyledTabButton>
              <StyledTabButton $active={activeTab === 'activityLog'} onClick={() => onTabChange('activityLog')}>
                Activity Logs
              </StyledTabButton>
              <StyledTabButton $active={activeTab === 'examStatusLog'} onClick={() => onTabChange('examStatusLog')}>
                Status Logs
              </StyledTabButton>
              <StyledTabButton $active={activeTab === 'uploadTracker'} onClick={() => onTabChange('uploadTracker')}>
                Uploads
              </StyledTabButton>
              <StyledTabButton $active={activeTab === 'sendQueue'} onClick={() => onTabChange('sendQueue')}>
                Queues
              </StyledTabButton>
              <StyledTabButton $active={activeTab === 'hl7Send'} onClick={() => onTabChange('hl7Send')}>
                Sends
              </StyledTabButton>
              <StyledTabButton $active={activeTab === 'hipaaLog'} onClick={() => onTabChange('hipaaLog')}>
                HIPAA Log
              </StyledTabButton>
            </>
          )}
        </StyledDivLeftControls>
        <StyledHorizontalSpacer />
        <StyledDivSendContainer $show={!isInternal || activeTab === 'files'}>
          <StyledDropdown
            disabled={changeCategoryMutation.isPending}
            data={_map(DocumentCategories)}
            defaultItem={{ name: 'Document Category' }}
            dataItemKey="value"
            textField="name"
            valueField="value"
            isForPrimitiveValues={true}
            value={file?.categoryId ?? DocumentCategories.EXAM.value}
            onChange={handleCategoryChange}
            valid
          />

          <StyledRotateWrapperDiv>
            <StyledRotateButtonDiv $disabled={rotateFileMutation.isPending} onClick={() => handleRotate(ROTATE_LEFT_DEGREES)}>
              {rotateFileMutation.isPending ? <Icon icon={faSpinner} spin={true} /> : <RotateLeftIcon height="18px" width="18px" />}
            </StyledRotateButtonDiv>
            <StyledRotateButtonDiv $disabled={rotateFileMutation.isPending} onClick={() => handleRotate(ROTATE_RIGHT_DEGREES)}>
              {rotateFileMutation.isPending ? <Icon icon={faSpinner} spin={true} /> : <RotateRightIcon height="18px" width="18px" />}
            </StyledRotateButtonDiv>
          </StyledRotateWrapperDiv>

          {isInternal && (
            <>
              <StyledDropdown
                data={getSendMethods()}
                dataItemKey="value"
                textField="name"
                defaultItem={{ name: 'Send Document Via' }}
                filterable={false}
                value={contactType?.value}
                onChange={handleContactDropdownChange}
                isForPrimitiveValues={true}
                valueField="value"
                valid
              />
              <StyledInput
                disabled={contactType?.value === 'EHR Integration' || contactType?.value === 'Dicom' || typeof contactType?.value === 'number'}
                name="contact"
                onChange={handleContactInputChange}
                placeholder={
                  contactType?.value === 'EHR Integration' || contactType?.value === 'Dicom' || typeof contactType?.value === 'number'
                    ? ''
                    : 'Enter contact Here'
                }
                valid
                value={contact}
              />
              <Tooltip text={contactType?.value === 'Dicom' && !hasSuid ? 'Exam missing SUID' : ''}>
                <span>
                  <StyledSendButton disabled={sendButtonDisabled} onClick={handleSendClick} size={ComponentSizes.SMALL}>
                    {sendDocumentsMutation.isPending ? <Icon icon={faSpinner} spin={true} /> : 'Send'}
                  </StyledSendButton>
                </span>
              </Tooltip>
              {ShowGenerateReport && (
                <Button disabled={!file || !hasReadHistory || generateReportMutation.isPending} onClick={handleGenerateReport} size={ComponentSizes.SMALL}>
                  {generateReportMutation.isPending ? <Icon icon={faSpinner} spin={true} /> : 'Generate Report'}
                </Button>
              )}
            </>
          )}
        </StyledDivSendContainer>
      </StyledControlsWrapper>
    </StyledDivHeaderWrapper>
  );
};

export { ExamProcessingHeader };
